/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { IProjectDataFacade } from '../../../../../data-access/src/lib/domain/facades-models/project-data-facade-interface';
import { ProjectDataService } from '../../../../../utils/src';
import { GlobalConstants } from '../../constants/global-constants';
import {
  IFamilyInformationItem,
  Param,
  ParamSize,
} from '../../components/add-family-information/add-family-information-types';
import { Observable } from 'rxjs';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { Store } from '@ngrx/store';
import { sendPopupRoutingAction } from '../toolbelt.actions';
import { ProjectDataState } from './project-data-reducer';
import {
  GetBuiltInGroupParameters,
  GetDefinitionGroupParameters,
  GetParamCategory,
  InitializeAddProjectParameters,
} from './project-data-action';
import { ParamCategory } from '../../models/add-project-parameter-category';
import {
  selectAddProjectParameterData,
  selectBuiltInProjectParameter,
  selectDefinitionGroupProjectParameter,
  selectParamCategories,
} from './project-data-selector';
import { ProjectParameterDataItems } from '@hcworkspace/sysque/toolbelt/feature';
import { ProjectDataParamInfo } from '../../models/add-project-data-params-info';
import { ProjectParameterInfo } from '../../models/add-project-parameter-info';
import { AddProjectDataReceiveController } from '../../receive-controllers/add-project-parameter.receive-controller';

@Injectable({
  providedIn: 'root',
})
export class ProjectDataFacade implements IProjectDataFacade {
  constructor(
    public projectDataService: ProjectDataService,
    private localStorageService: LocalStorageService,
    private readonly receiveMessageHandler: ReceiveMessageHandler,
    private store: Store<ProjectDataState>
  ) {
    this.receiveMessageHandler.registerController(new AddProjectDataReceiveController(this.localStorageService));
  }

  public getAddProjectParameterResponse(): Param[] {
    return this.localStorageService.getStorage(GlobalConstants.ADD_PROJECT_PARAMETER_RESPONSE);
  }

  public getProjectParametersNeedToModify(needParam: boolean): Param | Param[] {
    if (!needParam) {
      return this.localStorageService.getStorage(GlobalConstants.PROJECT_PARAM_TO_MODIFY).paramList;
    }
    return this.localStorageService.getStorage(GlobalConstants.PROJECT_PARAM_TO_MODIFY).needToEdit;
  }

  public addProjectParameter(projectParameterInfo: ProjectParameterInfo): void {
    this.projectDataService.addProjectParameter(projectParameterInfo);
  }

  public getDefinitionGroupParameters(): Observable<ProjectDataParamInfo[]> {
    this.store.dispatch(GetDefinitionGroupParameters());
    return this.store.select(selectDefinitionGroupProjectParameter);
  }

  public getBuiltInGroupParameters(): Observable<ProjectDataParamInfo[]> {
    this.store.dispatch(GetBuiltInGroupParameters());
    return this.store.select(selectBuiltInProjectParameter);
  }

  public initializeAddProjectParametersFromJson(): Observable<ProjectParameterDataItems[]> {
    this.store.dispatch(InitializeAddProjectParameters());
    return this.store.select(selectAddProjectParameterData);
  }

  public selectParamCategories(): Observable<ParamCategory[]> {
    return this.store.select(selectParamCategories);
  }

  public getParamCategories(currentCategory: string): Observable<ParamCategory[]> {
    this.store.dispatch(GetParamCategory({ currentCategory: currentCategory }));
    return this.store.select(selectParamCategories);
  }

  public getEditFamilyItem(): IFamilyInformationItem {
    return this.localStorageService.getStorage(GlobalConstants.FAMILY_INFORMATION_ITEM);
  }

  public exportExcelClick(parameterSizes: ParamSize[], fileName: string): void {
    this.projectDataService.exportExcelClick(parameterSizes, fileName);
  }

  public importExcelClick(parameterSizes: ParamSize[]): void {
    this.projectDataService.importExcelClick(parameterSizes);
  }

  public changeDialogDimensions(height: number): void {
    this.projectDataService.changeDialogDimensions(height);
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public readParametersFromDocument(selectedCategory: string): Observable<Param[]> {
    return this.projectDataService.readParametersFromDocument(selectedCategory);
  }

  public getStorage(key: string): string {
    return this.localStorageService.getStorage(key);
  }

  public getFamilyInformationResponse(): IFamilyInformationItem {
    return this.localStorageService.getStorage(GlobalConstants.FAMILY_INFORMATION_ITEM) as IFamilyInformationItem;
  }

  public sendPopupRoutingAction(route: string): void {
    let sysquePopupConfig = new SysquePopupConfig();
    createDialog(sysquePopupConfig, route);
    this.store.dispatch(
      sendPopupRoutingAction({
        route: route,
        width: sysquePopupConfig.width,
        height: sysquePopupConfig.height,
      })
    );
  }

  public closeWindow(): void {
    this.projectDataService.onCloseWindow();
  }
}
